import { useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";

const PROBLEM_COLORS = {
  "forced rhyme": "text-amber-200 bg-amber-500/15 border-amber-400/30",
  "weak hook": "text-rose-200 bg-rose-500/15 border-rose-400/30",
  "weak image": "text-sky-200 bg-sky-500/15 border-sky-400/30",
  grammar: "text-emerald-200 bg-emerald-500/15 border-emerald-400/30",
  "unnatural phrasing": "text-violet-200 bg-violet-500/15 border-violet-400/30",
  "cliché": "text-orange-200 bg-orange-500/15 border-orange-400/30",
  singability: "text-cyan-200 bg-cyan-500/15 border-cyan-400/30",
};

function problemClass(problem = "") {
  const key = Object.keys(PROBLEM_COLORS).find((k) => problem.toLowerCase().includes(k));
  return PROBLEM_COLORS[key] || "text-white/80 bg-white/10 border-white/15";
}

/** Collapsible "What I improved" panel shown after the write → review → improve pass. */
export function LyricsReviewPanel({ review, modelLabel, notes = [], onDismiss, defaultOpen = false, testId = "lyrics-review-panel" }) {
  const [open, setOpen] = useState(defaultOpen);
  const issues = Array.isArray(review?.issues) ? review.issues : [];
  const strengths = Array.isArray(review?.strengths) ? review.strengths : [];
  if (!review && notes.length === 0) return null;
  const headline = review?.summary || (notes.length ? notes[notes.length - 1] : "");
  return (
    <div data-testid={testId} className="mb-4 overflow-hidden rounded-2xl border border-fuchsia-400/25 bg-fuchsia-500/[0.07]">
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-fuchsia-500/10"
      >
        <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-fuchsia-500/25 text-fuchsia-100"><Sparkles className="h-4 w-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-fuchsia-100">
            What I improved
            {review?.chorus_score != null ? <span className="rounded-md bg-black/30 px-1.5 py-0.5 normal-case tracking-normal text-fuchsia-200">chorus {review.chorus_score}/10 before edits</span> : null}
            {review?.overall_score != null ? <span className="rounded-md bg-black/30 px-1.5 py-0.5 normal-case tracking-normal text-fuchsia-200">overall {review.overall_score}/10</span> : null}
            {issues.length ? <span className="rounded-md bg-black/30 px-1.5 py-0.5 normal-case tracking-normal text-white/80">{issues.length} fix{issues.length === 1 ? "" : "es"}</span> : null}
            {modelLabel ? <span className="normal-case tracking-normal text-white/50">· {modelLabel}</span> : null}
          </span>
          {headline ? <span data-testid={`${testId}-summary`} className="mt-1 block text-sm leading-relaxed text-white/85">{headline}</span> : null}
        </span>
        <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-white/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <div className="border-t border-fuchsia-400/15 px-4 py-3">
          {strengths.length ? (
            <ul className="mb-3 space-y-1 text-sm text-emerald-100/90">
              {strengths.map((s, i) => <li key={`st-${i}`} className="flex gap-2"><span className="text-emerald-300">+</span><span>{s}</span></li>)}
            </ul>
          ) : null}
          {notes.length ? (
            <ul className="mb-3 space-y-1 text-sm text-white/80">
              {notes.map((n, i) => <li key={`n-${i}`} data-testid={`${testId}-note-${i}`} className="flex gap-2"><span className="text-fuchsia-300">•</span><span>{n}</span></li>)}
            </ul>
          ) : null}
          {issues.length ? (
            <ul className="space-y-2">
              {issues.map((it, i) => (
                <li key={`is-${i}`} data-testid={`${testId}-issue-${i}`} className="rounded-xl border border-white/10 bg-black/25 p-3">
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <span className={`rounded-md border px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wider ${problemClass(it.problem)}`}>{it.problem || "improved"}</span>
                    {it.section ? <span className="font-mono text-[11px] uppercase tracking-wider text-white/50">[{String(it.section).replace(/^\[+|\]+$/g, "")}]</span> : null}
                  </div>
                  {it.line ? <p className="text-sm text-white/55 line-through decoration-white/30">{it.line}</p> : null}
                  {it.fix ? <p className="text-sm text-white">{it.fix}</p> : null}
                </li>
              ))}
            </ul>
          ) : (
            !notes.length && !strengths.length ? <p className="text-sm text-white/60">No line-level issues were found.</p> : null
          )}
          {onDismiss ? (
            <button type="button" data-testid={`${testId}-dismiss`} onClick={onDismiss} className="mt-3 font-mono text-[11px] uppercase tracking-wider text-white/50 hover:text-white">Dismiss</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default LyricsReviewPanel;
